/**
 * Modal „Zaproś pracownika" (spec 01 §18) — dwa kroki: formularz i link.
 *
 * Kod zaproszenia przychodzi z serwera JEDEN raz (w bazie leży wyłącznie jego
 * hash), więc krok 2 jest ostatnią chwilą, żeby go skopiować albo wysłać —
 * mówimy o tym wprost zamiast udawać, że link da się odtworzyć później.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Check,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  useToast,
} from '@k1p4/ui'
import { CONTRACT_TYPES } from '@k1p4/shared'
import { formatDate } from '@k1p4/i18n'
import {
  ApiError,
  api,
  apiToday,
  type ContractType,
  type CreatedInvitation,
  type InviteBody,
} from '../../api/client'
import styles from './InviteModal.module.css'

interface FormValues {
  firstName: string
  lastName: string
  email: string
  departmentName: string
  position: string
  startDate: string
  employmentType: ContractType | ''
  managerName: string
}

const EMPTY: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  departmentName: '',
  position: '',
  startDate: '',
  employmentType: '',
  managerName: '',
}

export interface InviteModalProps {
  open: boolean
  onClose: () => void
  /** Wywoływane po wygenerowaniu zaproszenia — lista pracowników się odświeża. */
  onCreated?: () => void
}

/** Oczekujące zaproszenie, które blokuje adres — z ciała odpowiedzi 409 (Fala 6, grupa 32). */
interface PendingInvitation {
  id: string
  expiresAt: string
}

function pendingFrom(error: ApiError): PendingInvitation | null {
  const { invitationId, expiresAt } = error.details
  return typeof invitationId === 'string' && typeof expiresAt === 'string'
    ? { id: invitationId, expiresAt }
    : null
}

export function InviteModal({ open, onClose, onCreated }: InviteModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    firstName: useId(),
    lastName: useId(),
    email: useId(),
    department: useId(),
    position: useId(),
    startDate: useId(),
    employmentType: useId(),
    manager: useId(),
    link: useId(),
  }

  const [values, setValues] = useState<FormValues>(EMPTY)
  const [invitation, setInvitation] = useState<CreatedInvitation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingInvitation | null>(null)

  const set = (patch: Partial<FormValues>) => {
    setValues((current) => ({ ...current, ...patch }))
    setError(null)
    setPending(null)
  }

  function close(): void {
    setValues(EMPTY)
    setInvitation(null)
    setError(null)
    setPending(null)
    onClose()
  }

  function body(): InviteBody {
    return {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      ...(values.departmentName.trim() ? { departmentName: values.departmentName.trim() } : {}),
      ...(values.position.trim() ? { position: values.position.trim() } : {}),
      ...(values.startDate ? { startDate: values.startDate } : {}),
      ...(values.employmentType ? { employmentType: values.employmentType } : {}),
      ...(values.managerName.trim() ? { managerName: values.managerName.trim() } : {}),
    }
  }

  function created(next: CreatedInvitation): void {
    setPending(null)
    setInvitation(next)
    onCreated?.()
    void queryClient.invalidateQueries({ queryKey: ['invitations'] })
  }

  const create = useMutation({
    mutationFn: () => api.createInvitation(body()),
    onSuccess: created,
    onError: (caught) => {
      /*
       * Adres ma już oczekujące zaproszenie: zamiast „spróbuj ponownie" modal
       * mówi, do kiedy jest ważne, i proponuje odwołanie z wystawieniem nowego.
       */
      const blocking =
        caught instanceof ApiError && caught.code === 'INVITATION_PENDING' ? pendingFrom(caught) : null
      if (blocking) {
        setPending(blocking)
        return
      }
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'EMAIL_TAKEN'
            ? 'invite.errorTaken'
            : caught.code === 'EMAIL_NOT_VERIFIED'
              ? 'invite.errorEmailUnverified'
              : 'invite.errorGeneric',
        )
      } else {
        setError('invite.errorGeneric')
      }
    },
  })

  /* Stary kod przestaje działać (DELETE z wpisem w dzienniku), dopiero potem powstaje nowy. */
  const reissue = useMutation({
    mutationFn: async () => {
      await api.revokeInvitation(pending!.id)
      return api.createInvitation(body())
    },
    onSuccess: created,
    onError: () => {
      setPending(null)
      setError('invite.errorGeneric')
    },
  })

  const send = useMutation({
    mutationFn: () => api.sendInvitationEmail(invitation!.id, invitation!.code),
    onSuccess: () => {
      toast.show(t('invite.sent'), { tone: 'success' })
      close()
    },
    onError: (cause) =>
      setError(cause instanceof ApiError && cause.code === 'MAIL_NOT_SENT' ? 'invite.errorMailNotSent' : 'invite.errorGeneric'),
  })

  function generate(): void {
    if (
      values.firstName.trim().length === 0 ||
      values.lastName.trim().length === 0 ||
      !values.email.includes('@')
    ) {
      setError('invite.required')
      return
    }
    create.mutate()
  }

  async function copyLink(): Promise<void> {
    if (!invitation) return
    try {
      await navigator.clipboard.writeText(invitation.joinUrl)
      toast.show(t('invite.copied'), { tone: 'success' })
    } catch {
      /* Odmowa dostępu do schowka nie może wyglądać jak sukces. */
      setError('invite.errorGeneric')
    }
  }

  return (
    <Modal open={open} onClose={close} title={t('invite.title')}>
      <p className={styles.subtitle}>{t('invite.subtitle')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {pending !== null && invitation === null ? (
        <>
          <InfoBanner tone="warning" className={styles.banner}>
            {t(
              new Date(pending.expiresAt) <= apiToday()
                ? 'invite.errorPendingExpired'
                : 'invite.errorPending',
              { date: formatDate(new Date(pending.expiresAt), 'dot') },
            )}
          </InfoBanner>
          <Button
            variant="secondary"
            className={styles.reissue}
            disabled={reissue.isPending}
            onClick={() => reissue.mutate()}
          >
            {reissue.isPending ? <Spinner label={t('invite.generating')} /> : t('invite.revokeAndReissue')}
          </Button>
        </>
      ) : null}

      {invitation === null ? (
        <>
          <div className={styles.grid}>
            <FormField label={t('invite.firstName')} htmlFor={ids.firstName}>
              <Input
                placeholder={t('invite.firstNamePlaceholder')}
                value={values.firstName}
                onChange={(event) => set({ firstName: event.target.value })}
              />
            </FormField>
            <FormField label={t('invite.lastName')} htmlFor={ids.lastName}>
              <Input
                placeholder={t('invite.lastNamePlaceholder')}
                value={values.lastName}
                onChange={(event) => set({ lastName: event.target.value })}
              />
            </FormField>
            <FormField className={styles.full} label={t('invite.email')} htmlFor={ids.email}>
              <Input
                type="email"
                placeholder={t('invite.emailPlaceholder')}
                value={values.email}
                onChange={(event) => set({ email: event.target.value })}
              />
            </FormField>
            <FormField label={t('invite.department')} htmlFor={ids.department}>
              <Input
                placeholder={t('invite.departmentPlaceholder')}
                value={values.departmentName}
                onChange={(event) => set({ departmentName: event.target.value })}
              />
            </FormField>
            <FormField label={t('invite.position')} htmlFor={ids.position}>
              <Input
                placeholder={t('invite.positionPlaceholder')}
                value={values.position}
                onChange={(event) => set({ position: event.target.value })}
              />
            </FormField>
            <FormField label={t('invite.startDate')} htmlFor={ids.startDate}>
              <Input
                type="date"
                value={values.startDate}
                onChange={(event) => set({ startDate: event.target.value })}
              />
            </FormField>
            <FormField label={t('invite.employmentType')} htmlFor={ids.employmentType}>
              <Select
                value={values.employmentType}
                onChange={(event) => set({ employmentType: event.target.value as ContractType })}
              >
                <option value="">{t('invite.employmentTypeNone')}</option>
                {CONTRACT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`contractType.${type}`)}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField className={styles.full} label={t('invite.manager')} htmlFor={ids.manager}>
              <Input
                placeholder={t('invite.managerPlaceholder')}
                value={values.managerName}
                onChange={(event) => set({ managerName: event.target.value })}
              />
            </FormField>
          </div>

          <Button size="lg" className={styles.full} disabled={create.isPending} onClick={generate}>
            {create.isPending ? <Spinner label={t('invite.generating')} /> : t('invite.generate')}
          </Button>
        </>
      ) : (
        <>
          <p className={styles.success}>
            <Check size={16} />
            {t('invite.generated')}
          </p>

          <FormField label={t('invite.linkLabel')} htmlFor={ids.link}>
            {/* Własne id wiersza — bez niego FormField nadałby je diva zamiast pola. */}
            <div id={`${ids.link}-row`} className={styles.linkRow}>
              <Input id={ids.link} readOnly value={invitation.joinUrl} className={styles.linkInput} />
              <Button variant="secondary" onClick={copyLink}>
                {t('invite.copy')}
              </Button>
            </div>
          </FormField>

          <div className={styles.boxes}>
            <Card pad="sm">
              <p className={styles.boxLabel}>{t('invite.statusLabel')}</p>
              <p className={styles.boxValue}>
                <span className={styles.dotPending} aria-hidden="true" />
                {t('invite.statusPending')}
              </p>
            </Card>
            <Card pad="sm">
              <p className={styles.boxLabel}>{t('invite.validityLabel')}</p>
              <p className={styles.boxValue}>
                {t('invite.validity', { date: formatDate(new Date(invitation.expiresAt), 'dot') })}
              </p>
            </Card>
          </div>

          <p className={styles.note}>{t('invite.note')}</p>
          {invitation.employeeId ? (
            <p className={styles.note}>{t('invite.resumesProcess')}</p>
          ) : null}
          <p className={styles.once}>{t('invite.codeOnce')}</p>

          <div className={styles.actions}>
            <Button variant="secondary" size="lg" onClick={close}>
              {t('invite.back')}
            </Button>
            <Button
              size="lg"
              className={styles.grow}
              disabled={send.isPending}
              onClick={() => send.mutate()}
            >
              {send.isPending ? <Spinner label={t('invite.sending')} /> : t('invite.send')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
